import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

/**
 * Общий курсор Анализа: одна дата под пальцем — все карточки про неё.
 *
 * ЗАЧЕМ. Карточки Анализа отвечают на разные вопросы об одном и том же дне:
 * сколько было нагрузки, что показывал пульс покоя, где стояла форма. Пока
 * курсор жил внутри каждой карточки, связать их можно было только глазами и
 * памятью — «а какой это был день на предыдущем графике». Это и есть работа,
 * которую делает инструмент, а не человек: провёл по кривой формы — видишь
 * тот же день в самочувствии, объёме и нагрузке.
 *
 * ПОЧЕМУ ДАТА, А НЕ ИНДЕКС. У карточек разные ряды: у одной дни, у другой
 * недели, у третьей пропуски там, где не было тренировок. Индекс совпал бы
 * только у карточек с одинаковой сеткой, и совпадал бы НЕВЕРНО у остальных —
 * молча показывая четверг под курсором, стоящим на вторнике.
 *
 * ПОЧЕМУ КОНТЕКСТ, А НЕ СОСТОЯНИЕ СТРАНИЦЫ. Карточек около сорока, и они
 * живут в двух разных экранах (мобильная Студия и десктопная Аналитика).
 * Проброс через пропсы означал бы правку каждой карточки при каждом
 * изменении — и разъехался бы между экранами.
 */

interface CursorState {
  /** ISO-дата под курсором либо null, когда курсора нет ни на одной карточке. */
  date: string | null;
  /** id карточки, которая ведёт курсор: она рисует свой, остальные — общий. */
  ownerId: string | null;
  set: (id: string, date: string | null) => void;
  clear: (id: string) => void;
}

const Ctx = createContext<CursorState | null>(null);

export function AnalysisCursorProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ date: string | null; ownerId: string | null }>({
    date: null, ownerId: null,
  });

  const set = useCallback((id: string, date: string | null) => {
    setState((prev) =>
      prev.date === date && prev.ownerId === id ? prev : { date, ownerId: id },
    );
  }, []);

  // Уводит курсор ТОЛЬКО владелец. Иначе увод с одной карточки гасил бы
  // подсветку на всех — включая ту, где палец в этот момент стоит.
  const clear = useCallback((id: string) => {
    setState((prev) => (prev.ownerId === id ? { date: null, ownerId: null } : prev));
  }, []);

  const value = useMemo<CursorState>(
    () => ({ date: state.date, ownerId: state.ownerId, set, clear }),
    [state.date, state.ownerId, set, clear],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export interface SharedCursor {
  /** Дата, которую эта карточка должна подсветить. null — подсвечивать нечего. */
  markerDate: string | null;
  /** Курсор ведёт эта карточка (её собственный tooltip уже всё показывает). */
  isOwner: boolean;
  /** Раскладывается прямо в recharts-график. */
  handlers: {
    onMouseMove: (e: { activeLabel?: string | number } | null) => void;
    onMouseLeave: () => void;
  };
}

/**
 * Подключение карточки к общему курсору.
 *
 * Возвращает готовые обработчики для recharts и дату, которую надо
 * подсветить. Владелец курсора свою дату НЕ подсвечивает: у него уже есть
 * tooltip, и вторая метка поверх него читалась бы как второе значение.
 *
 * Карточка без общего курсора продолжает работать как прежде — вне провайдера
 * хук отдаёт пустые обработчики, а не падает. Так карточку можно смонтировать
 * в тесте или на чужом экране, не таща за собой провайдер.
 */
export function useSharedCursor(cardId: string): SharedCursor {
  const ctx = useContext(Ctx);

  const handlers = useMemo(
    () => ({
      onMouseMove: (e: { activeLabel?: string | number } | null) => {
        if (!ctx) return;
        const label = e?.activeLabel;
        ctx.set(cardId, typeof label === "string" ? label : null);
      },
      onMouseLeave: () => ctx?.clear(cardId),
    }),
    [ctx, cardId],
  );

  if (!ctx) return { markerDate: null, isOwner: false, handlers };
  return {
    markerDate: ctx.ownerId === cardId ? null : ctx.date,
    isOwner: ctx.ownerId === cardId,
    handlers,
  };
}
